import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Box, Typography, GlobalStyles } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import en from "date-fns/locale/en-US";
import tr from "date-fns/locale/tr";
import fr from "date-fns/locale/fr";

// Register locales
registerLocale("en", en);
registerLocale("tr", tr);
registerLocale("fr", fr);

const ModernDatePicker = forwardRef(({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select date",
  label,
  error,
  helperText,
  required = false,
  disabled = false,
  withPortal = true,
  popperPlacement = "bottom-start",
  sx = {},
  ...props
}, ref) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { i18n } = useTranslation();
  
  // Get locale for datepicker
  const getDatePickerLocale = () => {
    const currentLang = i18n.language || 'en';
    if (currentLang.startsWith('tr')) return 'tr';
    if (currentLang.startsWith('fr')) return 'fr';
    return 'en';
  };

  // Convert string date to Date object
  const selectedDate = value ? new Date(value) : null;
  
  // Convert minDate string to Date object if provided
  const minDateObj = minDate ? new Date(minDate) : null;
  const maxDateObj = maxDate ? new Date(maxDate) : null;

  const handleDateChange = (date) => {
    if (date) {
      // Convert Date to YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      onChange(formattedDate);
    } else {
      onChange("");
    }
  };

  const CustomInput = forwardRef(({ value, onClick, placeholder, error: inputError, helperText: inputHelperText }, ref) => (
    <TextField
      fullWidth
      variant="filled"
      value={value || ""}
      onClick={onClick}
      inputRef={ref}
      placeholder={placeholder}
      error={inputError || error}
      helperText={inputHelperText || helperText}
      required={required}
      disabled={disabled}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <CalendarTodayIcon
            sx={{
              color: error ? colors.redAccent[400] : colors.blueAccent[300],
              fontSize: 24,
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                color: disabled ? colors.grey[600] : colors.blueAccent[400],
                transform: disabled ? "none" : "scale(1.1)",
              },
            }}
          />
        ),
      }}
      sx={{
        "& .MuiFilledInput-root": {
          backgroundColor: colors.primary[600],
          borderRadius: 2,
          border: `2px solid ${
            error
              ? `${colors.redAccent[500]}60`
              : `${colors.blueAccent[500]}40`
          }`,
          transition: "all 0.3s",
          "&:hover": {
            backgroundColor: disabled ? colors.primary[600] : colors.primary[500],
            borderColor: error
              ? colors.redAccent[400]
              : colors.blueAccent[400],
            cursor: disabled ? "not-allowed" : "pointer",
          },
          "&.Mui-focused": {
            backgroundColor: colors.primary[500],
            borderColor: error
              ? colors.redAccent[500]
              : colors.blueAccent[500],
            boxShadow: `0 0 0 3px ${
              error
                ? `${colors.redAccent[500]}33`
                : `${colors.blueAccent[500]}33`
            }`,
          },
          "&.Mui-disabled": {
            backgroundColor: colors.primary[700],
            borderColor: colors.grey[700],
            cursor: "not-allowed",
          },
        },
        "& .MuiFilledInput-input": {
          color: colors.grey[100],
          fontSize: "0.95rem",
          fontWeight: 500,
          padding: "14px",
          cursor: disabled ? "not-allowed" : "pointer",
        },
        "& .MuiFormHelperText-root": {
          color: colors.redAccent[400],
          fontSize: "0.75rem",
          marginTop: "4px",
        },
        ...sx,
      }}
    />
  ));

  CustomInput.displayName = "CustomInput";

  // Portal'da açılan takvim aynı modern stilleri kullansın (body'de render edildiği için Box sx uygulanmıyor)
  const portalStyles = {
    "#stats-datepicker-portal .react-datepicker": {
      fontFamily: "inherit",
      backgroundColor: colors.primary[600],
      border: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: "16px",
      boxShadow: "0 12px 48px rgba(0, 0, 0, 0.5)",
      overflow: "hidden",
    },
    "#stats-datepicker-portal .react-datepicker__header": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: "16px 16px 0 0",
      paddingTop: "20px",
    },
    "#stats-datepicker-portal .react-datepicker__current-month": {
      color: colors.grey[100],
      fontWeight: 700,
      fontSize: "1.1rem",
      marginBottom: "12px",
      textTransform: "capitalize",
    },
    "#stats-datepicker-portal .react-datepicker__day-name": {
      color: colors.grey[300],
      fontWeight: 600,
      fontSize: "0.9rem",
      width: "40px",
      lineHeight: "40px",
      margin: "2px",
      textTransform: "capitalize",
    },
    "#stats-datepicker-portal .react-datepicker__day": {
      color: colors.grey[100],
      width: "40px",
      height: "40px",
      lineHeight: "40px",
      borderRadius: "8px",
      margin: "2px",
      fontSize: "0.95rem",
      fontWeight: 500,
      transition: "all 0.2s ease",
    },
    "#stats-datepicker-portal .react-datepicker__day:hover": {
      backgroundColor: colors.blueAccent[600],
      borderRadius: "8px",
      transform: "scale(1.05)",
    },
    "#stats-datepicker-portal .react-datepicker__day--selected": {
      backgroundColor: colors.greenAccent[600],
      color: colors.grey[100],
      fontWeight: 700,
      boxShadow: `0 4px 12px ${colors.greenAccent[600]}60`,
    },
    "#stats-datepicker-portal .react-datepicker__day--today": {
      backgroundColor: colors.primary[500],
      border: `2px solid ${colors.greenAccent[500]}`,
      fontWeight: 700,
    },
    "#stats-datepicker-portal .react-datepicker__day--disabled": {
      color: colors.grey[600],
      opacity: 0.4,
      cursor: "not-allowed",
    },
    "#stats-datepicker-portal .react-datepicker__day--disabled:hover": {
      backgroundColor: "transparent",
      transform: "none",
    },
    "#stats-datepicker-portal .react-datepicker__day--outside-month": {
      color: colors.grey[600],
      opacity: 0.5,
    },
    "#stats-datepicker-portal .react-datepicker__navigation": {
      top: "20px",
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      backgroundColor: colors.greenAccent[600],
      border: "none",
      transition: "all 0.3s ease",
    },
    "#stats-datepicker-portal .react-datepicker__navigation:hover": {
      backgroundColor: colors.greenAccent[500],
      transform: "scale(1.1)",
    },
    "#stats-datepicker-portal .react-datepicker__navigation-icon::before": {
      borderColor: colors.grey[100],
      borderWidth: "2px 2px 0 0",
    },
    "#stats-datepicker-portal .react-datepicker__triangle": {
      display: "none",
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <GlobalStyles styles={portalStyles} />
      {label && (
        <Typography
          variant="caption"
          sx={{
            color: colors.grey[300],
            fontWeight: "500",
            fontSize: "0.875rem",
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          "& .react-datepicker-wrapper": {
            width: "100%",
          },
          "& .stats-datepicker-popper-high-z": {
            zIndex: 9999,
          },
          "& .react-datepicker__input-container": {
            width: "100%",
          },
          "& .react-datepicker": {
            fontFamily: "inherit",
            backgroundColor: colors.primary[600],
            border: `2px solid ${colors.blueAccent[500]}40`,
            borderRadius: "16px",
            boxShadow: `0 12px 48px rgba(0, 0, 0, 0.5)`,
            overflow: "hidden",
          },
          "& .react-datepicker__header": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: `2px solid ${colors.blueAccent[500]}40`,
            borderRadius: "16px 16px 0 0",
            paddingTop: "20px",
          },
          "& .react-datepicker__current-month": {
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: "1.1rem",
            marginBottom: "12px",
            textTransform: "capitalize",
            "&::first-letter": {
              textTransform: "uppercase",
            },
          },
          "& .react-datepicker__day-names": {
            marginBottom: "8px",
          },
          "& .react-datepicker__day-name": {
            color: colors.grey[300],
            fontWeight: 600,
            fontSize: "0.9rem",
            width: "40px",
            lineHeight: "40px",
            margin: "2px",
            textTransform: "capitalize",
            "&::first-letter": {
              textTransform: "uppercase",
            },
          },
          "& .react-datepicker__day": {
            color: colors.grey[100],
            width: "40px",
            height: "40px",
            lineHeight: "40px",
            borderRadius: "8px",
            margin: "2px",
            fontSize: "0.95rem",
            fontWeight: 500,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
              borderRadius: "8px",
              transform: "scale(1.05)",
            },
            "&.react-datepicker__day--selected": {
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontWeight: 700,
              boxShadow: `0 4px 12px ${colors.greenAccent[600]}60`,
            },
            "&.react-datepicker__day--today": {
              backgroundColor: colors.primary[500],
              border: `2px solid ${colors.greenAccent[500]}`,
              fontWeight: 700,
            },
            "&.react-datepicker__day--disabled": {
              color: colors.grey[600],
              opacity: 0.4,
              cursor: "not-allowed",
              "&:hover": {
                backgroundColor: "transparent",
                transform: "none",
              },
            },
            "&.react-datepicker__day--outside-month": {
              color: colors.grey[600],
              opacity: 0.5,
            },
          },
          "& .react-datepicker__navigation": {
            top: "20px",
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            backgroundColor: colors.greenAccent[600],
            border: "none",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: colors.greenAccent[500],
              transform: "scale(1.1)",
            },
            "&:focus": {
              outline: "none",
            },
          },
          "& .react-datepicker__navigation-icon::before": {
            borderColor: colors.grey[100],
            borderWidth: "2px 2px 0 0",
          },
          "& .react-datepicker__month": {
            margin: "12px",
            padding: "8px",
          },
          "& .react-datepicker__triangle": {
            display: "none",
          },
        }}
      >
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          minDate={minDateObj}
          maxDate={maxDateObj}
          placeholderText={placeholder}
          dateFormat="yyyy-MM-dd"
          locale={getDatePickerLocale()}
          customInput={<CustomInput error={error} placeholder={placeholder} helperText={helperText} required={required} disabled={disabled} />}
          disabled={disabled}
          showPopperArrow={false}
          withPortal={withPortal}
          popperPlacement={popperPlacement}
          {...props}
        />
      </Box>
    </Box>
  );
});

ModernDatePicker.displayName = "ModernDatePicker";

export default ModernDatePicker;

